import { useMemo } from "react";

import type { TalentFiltersFetchParams } from "@js/apps/common/components/filters";
import {
  FiltersBottomActionsContainer,
  FiltersBottomContainer,
  FiltersCarousel,
  FiltersCarouselContainer,
  FiltersClearAllButton,
  FiltersSortButton,
  ResultsCount,
} from "@js/apps/common/components/filters/components";
import { SEARCH_FILTER_DATA_TYPES } from "@js/apps/common/components/filters/constants";
import {
  RoleFilter,
  SkillsFilter,
} from "@js/apps/common/components/filters/fields";
import { FeedbackFilter } from "@js/apps/common/components/filters/fields/feedback-filter";
import { YearsOfExperienceFilter } from "@js/apps/common/components/filters/fields/years-of-experience-filter";
import { SAVED_FILTERS_ENDPOINT } from "@js/apps/common/components/save-filters/const";
import { SaveTalentsFilters } from "@js/apps/common/components/save-filters/save-talents-filters";
import { useAccountType, useIsNodeStaff } from "@js/apps/common/hooks";
import { TALENT_LOCATION } from "@js/apps/freelancer/constants";
import { TalentLocationFilter } from "@js/apps/freelancer/forms/fields/talent-location-filter";
import {
  ApprovalStatusFilter,
  AvailabilityFilter,
} from "@js/apps/freelancer/views/talent-list/fields";
import { savedFilterLoaded } from "@js/apps/jobs/apps/listing/actions";
import { useGetActiveFilterName } from "@js/apps/jobs/apps/listing/hooks/active-filter-name";
import { useResetUniversalSearchPhrase } from "@js/apps/universal-search/universal-search-slice";
import { useAppDispatch, useNavigate } from "@js/hooks";
import { LocalStorage } from "@js/services";
import { isNotNullable } from "@js/utils";

type SearchTalentsListFiltersProps = {
  isAnyFilterApplied: boolean;
  count: number;
  total_count?: number;
  filters: Partial<TalentFiltersFetchParams>;
};

const location = TALENT_LOCATION.talent_search;

export const SearchTalentsListFilters = ({
  isAnyFilterApplied,
  count,
  total_count,
  filters,
}: SearchTalentsListFiltersProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isNodeStaff = useIsNodeStaff();
  const { isEmployer } = useAccountType();
  const { savedFilterName } = useGetActiveFilterName(
    location,
    SAVED_FILTERS_ENDPOINT.TALENTS,
  );
  const clearUniversalSearch = useResetUniversalSearchPhrase();

  const items = useMemo(
    () => getTalentsFiltersItems({ isNodeStaff, isEmployer }),
    [isNodeStaff, isEmployer],
  );

  const clearFilters = () => {
    clearUniversalSearch();
    dispatch(
      savedFilterLoaded({
        location,
        name: null,
      }),
    );
    LocalStorage.removeItem(LocalStorage.keys.TALENT_SEARCH_FILTERS);
    navigate(window.location.pathname);
  };

  return (
    <>
      <FiltersCarouselContainer>
        <FiltersCarousel items={items} />
      </FiltersCarouselContainer>
      <FiltersBottomContainer>
        <FiltersBottomActionsContainer>
          <ResultsCount
            dataType={SEARCH_FILTER_DATA_TYPES.talent}
            count={count}
            total_count={total_count}
          />
          {isAnyFilterApplied && (
            <>
              <SaveTalentsFilters
                savedFilterName={savedFilterName}
                filters={filters}
                location={location}
              />
              <FiltersClearAllButton onClick={clearFilters} />
            </>
          )}
        </FiltersBottomActionsContainer>
        <FiltersSortButton location={location} defaultValue="-score" />
      </FiltersBottomContainer>
    </>
  );
};

const getTalentsFiltersItems = ({
  isNodeStaff,
  isEmployer,
}: {
  isNodeStaff: boolean;
  isEmployer: boolean;
}) => {
  return [
    <RoleFilter key="role-filter" />,
    <SkillsFilter key="skills-filter" />,
    <TalentLocationFilter key="talent-location-filter" />,
    isNodeStaff ? <ApprovalStatusFilter key="approval-status-filter" /> : null,
    isEmployer || isNodeStaff ? (
      <AvailabilityFilter key="availability-filter" />
    ) : null,
    <YearsOfExperienceFilter
      key="years-of-experience-filter"
      location={location}
    />,
    isNodeStaff ? <FeedbackFilter key="feedback-filter" /> : null,
  ].filter(isNotNullable);
};
